import { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import {
    useProduct,
    useProducts,
    useUpdateProduct,
    useSetProductFlag,
    useDeleteProduct,
    useRemoveFromCategory,
    AddToSetModal,
    ManageVariantsModal,
    ManageProductExtrasModal,
    AddToCategoryModal,
    SetDiscountModal,
    EditProductModal,
    ManageProductStocksModal,
    PhotosBeforeShowModal,
    ProductFlagType,
    productThumbSrc,
} from '@modules/products';
import { Breadcrumbs, type BreadcrumbItem } from '@components';
import { sellerFinanceApi } from '@modules/seller-finance/api/seller-finance.api';
import { sellerHelpUrl } from '@shared/utils/seller-help-url';
import {
    IoArrowUp,
    IoChevronDown,
    IoChevronUp,
    IoCartOutline,
    IoCreateOutline,
    IoCubeOutline,
    IoEyeOffOutline,
    IoEyeOutline,
    IoInformationCircleOutline,
    IoPricetagOutline,
} from 'react-icons/io5';
import { BackButton, Button, CachedImage, DoubleCheckModal, Input, Modal, RefetchButton, Switch } from '@shared/ui';
import { useIsMobile } from '@shared/hooks';
import ProductPrice from '@modules/products/components/ProductCard/ProductPrice';
import ProductBadges from '@modules/products/components/ProductCard/ProductBadges';
import { usePickupPoints } from '@modules/pickup-points';
import {
    ExtraRuleTypeFrom,
    ExtraRuleTypeTo,
    useExtras,
} from '@modules/extras';
import '@shared/styles/page.css';
import styles from './ProductPage.module.css';
import { Loader } from '@mantine/core';

function formatRub(n: number): string {
    return new Intl.NumberFormat('ru-RU', {
        maximumFractionDigits: 2,
    }).format(n);
}

function ProductField({
    label,
    children,
}: {
    label: string;
    children: React.ReactNode;
}) {
    return (
        <div className={styles.row}>
            <div className={styles.rowLabel}>{label}</div>
            <div className={styles.rowValue}>{children}</div>
        </div>
    );
}

export default function ProductPage() {
    const { productId } = useParams<{ productId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const isMobile = useIsMobile();
    const productIdNum = productId ? parseInt(productId, 10) : undefined;

    const {
        data: product,
        isLoading: productLoading,
        isFetching: productFetching,
        error: productError,
        refetch: refetchProduct,
    } = useProduct(productIdNum!);
    const { data: pickupPoints = [], isLoading: pickupPointsLoading } = usePickupPoints();
    const hasPickupPoints = pickupPoints.length > 0;
    const { data: extrasRules = [], refetch: refetchExtras } = useExtras();
    const { data: allProducts = [] } = useProducts();
    const queryClient = useQueryClient();

    const productExtras = useMemo(() => {
        if (!productIdNum) return [];
        const dedicated = extrasRules.find(
            (rule) =>
                rule.typeTo === ExtraRuleTypeTo.PRODUCTS &&
                rule.typeFrom === ExtraRuleTypeFrom.PRODUCTS &&
                Array.isArray(rule.idsTo) &&
                rule.idsTo.map(Number).length === 1 &&
                Number(rule.idsTo[0]) === productIdNum,
        );
        if (!dedicated) return [];
        const fromIds = (Array.isArray(dedicated.idsFrom) ? dedicated.idsFrom : [])
            .map(Number)
            .slice(0, 5);
        const byId = new Map(allProducts.map((p) => [p.id, p]));
        return fromIds.map((id) => byId.get(id)).filter(Boolean) as typeof allProducts;
    }, [extrasRules, allProducts, productIdNum]);

    const [showDiscountModal, setShowDiscountModal] = useState(false);
    const [isAddToSetModalOpen, setIsAddToSetModalOpen] = useState(false);
    const [showManageVariantsModal, setShowManageVariantsModal] = useState(false);
    const [showManageExtrasModal, setShowManageExtrasModal] = useState(false);
    const [showAddToCategoryModal, setShowAddToCategoryModal] = useState(false);
    const [showManageStocksModal, setShowManageStocksModal] = useState(false);
    const [showPhotosBeforeShowModal, setShowPhotosBeforeShowModal] = useState(false);
    const [showModerationInfoModal, setShowModerationInfoModal] = useState(false);
    const [showModerationAfterStocks, setShowModerationAfterStocks] = useState(false);
    const [stocksModalAfterCreateHint, setStocksModalAfterCreateHint] = useState(false);
    const [stocksModalBeforeShowHint, setStocksModalBeforeShowHint] = useState(false);
    const [showEditProductModal, setShowEditProductModal] = useState(false);
    const [infoTab, setInfoTab] = useState<'description' | 'composition' | 'specs'>('description');
    const [previewImageId, setPreviewImageId] = useState<number | null>(null);
    const thumbsTrackRef = useRef<HTMLDivElement>(null);
    const [isDeleteCategoryModalOpen, setIsDeleteCategoryModalOpen] = useState(false);
    const [categoryToRemove, setCategoryToRemove] = useState<{ id: number; name: string } | null>(null);
    const [isDeleteProductModalOpen, setIsDeleteProductModalOpen] = useState(false);
    const [showCpcPromotionModal, setShowCpcPromotionModal] = useState(false);
    const [showCpcInfoModal, setShowCpcInfoModal] = useState(false);

    const setHiddenMutation = useSetProductFlag(ProductFlagType.HIDDEN);
    const deleteMutation = useDeleteProduct();
    const removeFromCategory = useRemoveFromCategory();
    const updateProductMutation = useUpdateProduct();

    const [cpcPromotionRub, setCpcPromotionRub] = useState('');
    const [cpcPromotionEnabled, setCpcPromotionEnabled] = useState(false);

    const { data: advertisingCredit, isLoading: advertisingCreditLoading } =
        useQuery({
            queryKey: ['seller-advertising-credit'],
            queryFn: () => sellerFinanceApi.getMyAdvertisingCredit(),
            enabled: Boolean(product?.sellerId) && showCpcPromotionModal,
            staleTime: 30_000,
        });

    const cpcCreditExhausted =
        advertisingCredit != null && advertisingCredit.availableForAdsRub <= 0;
    const cpcBudgetPaused = Boolean(advertisingCredit?.weeklyBudget?.paused);
    const cpcAdsBlocked = cpcBudgetPaused;
    const parsedPromotionCpc = (() => {
        const normalized = cpcPromotionRub.trim().replace(',', '.');
        const parsed = normalized === '' ? 0 : parseFloat(normalized);
        return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
    })();
    const cpcAboveMaxBid =
        advertisingCredit != null &&
        parsedPromotionCpc > advertisingCredit.cpcMaxBidRub;
    const cpcBelowMinBid =
        advertisingCredit != null &&
        parsedPromotionCpc < advertisingCredit.cpcMinBidRub;
    const cpcMinBidLabel =
        advertisingCredit != null ? formatRub(advertisingCredit.cpcMinBidRub) : null;
    const cpcMaxBidLabel =
        advertisingCredit != null ? formatRub(advertisingCredit.cpcMaxBidRub) : null;
    const cpcBidRangeDescription =
        cpcMinBidLabel != null && cpcMaxBidLabel != null
            ? `От ${cpcMinBidLabel} до ${cpcMaxBidLabel} ₽`
            : undefined;
    const cpcEnableLocked =
        !cpcPromotionEnabled &&
        (cpcAdsBlocked || cpcAboveMaxBid || cpcBelowMinBid);

    // Сейчас 1 мин для теста; в проде вернуть 15.
    const PROMOTION_SETTINGS_COOLDOWN_MS = 1 * 60 * 1000;
    const promotionChangedAtMs = product?.promotionChangedAt
        ? new Date(product.promotionChangedAt).getTime()
        : null;
    const promotionCooldownRemainingMs =
        promotionChangedAtMs != null
            ? Math.max(
                0,
                PROMOTION_SETTINGS_COOLDOWN_MS - (Date.now() - promotionChangedAtMs),
            )
            : 0;
    const promotionSettingsOnCooldown = promotionCooldownRemainingMs > 0;
    const promotionCooldownMinutesLeft = Math.max(
        1,
        Math.ceil(promotionCooldownRemainingMs / 60_000),
    );
    // Получаем информацию о предыдущей странице
    const fromPath = location.state?.from as string | undefined;
    const fromOrder = location.state?.from === 'order';
    const orderId = location.state?.orderId as number | undefined;
    const openStocksModalOnLoad = location.state?.openStocksModalOnLoad === true;
    const showModerationInfoAfterStocks = location.state?.showModerationInfoAfterStocks === true;
    const showModerationInfoOnLoad = location.state?.showModerationInfoOnLoad === true;
    const openVariantsModalOnLoad = location.state?.openVariantsModalOnLoad === true;

    useEffect(() => {
        if (!product || !openStocksModalOnLoad) return;
        setShowManageStocksModal(true);
        setStocksModalAfterCreateHint(true);
        setShowModerationAfterStocks(showModerationInfoAfterStocks);

        const stateObj = (location.state ?? {}) as Record<string, unknown>;
        const {
            openStocksModalOnLoad: _openStocksFlag,
            showModerationInfoAfterStocks: _showModerationFlag,
            ...restState
        } = stateObj;
        void _openStocksFlag;
        void _showModerationFlag;
        navigate(location.pathname, { replace: true, state: restState });
    }, [
        product,
        openStocksModalOnLoad,
        showModerationInfoAfterStocks,
        navigate,
        location.pathname,
        location.state,
    ]);

    useEffect(() => {
        if (!product || !openVariantsModalOnLoad) return;
        setShowManageVariantsModal(true);
        const stateObj = (location.state ?? {}) as Record<string, unknown>;
        const { openVariantsModalOnLoad: _flag, ...restState } = stateObj;
        void _flag;
        navigate(location.pathname, { replace: true, state: restState });
    }, [product, openVariantsModalOnLoad, navigate, location.pathname, location.state]);

    useEffect(() => {
        if (!product || !showModerationInfoOnLoad) return;
        setShowModerationInfoModal(true);
        const stateObj = (location.state ?? {}) as Record<string, unknown>;
        const { showModerationInfoOnLoad: _flag, ...restState } = stateObj;
        void _flag;
        navigate(location.pathname, { replace: true, state: restState });
    }, [product, showModerationInfoOnLoad, navigate, location.pathname, location.state]);

    useEffect(() => {
        if (!product) return;
        setPreviewImageId(product.images?.[0]?.id ?? null);
    }, [product?.id]);

    useEffect(() => {
        if (!product) return;
        setCpcPromotionRub(product.cpcRub != null ? String(Number(product.cpcRub)) : '');
        setCpcPromotionEnabled(Boolean(product.promotionEnabled));
    }, [product?.id, product?.cpcRub, product?.promotionEnabled]);

    const handleCloseManageStocksModal = () => {
        setShowManageStocksModal(false);
        setStocksModalAfterCreateHint(false);
        setStocksModalBeforeShowHint(false);
        if (showModerationAfterStocks) {
            setShowModerationAfterStocks(false);
            setShowModerationInfoModal(true);
        }
    };

    const handleStocksSavedBeforeShow = async () => {
        if (!productIdNum) return;
        await setHiddenMutation.mutateAsync({
            id: productIdNum,
            value: false,
        });
        notifications.show({
            title: 'Успешно',
            message: 'Товар успешно показан',
            color: 'green',
        });
    };

    // Формируем breadcrumbs на основе предыдущей страницы
    const breadcrumbs: BreadcrumbItem[] = useMemo(() => {
        const crumbs: BreadcrumbItem[] = [
            { label: 'Главная', path: '/' },
            fromPath === '/promotion'
                ? {
                      label: 'Продвижение',
                      path: '/promotion',
                      state: location.state?.promotionTab
                          ? { promotionTab: location.state.promotionTab }
                          : undefined,
                  }
                : { label: 'Товары', path: '/products' },
        ];

        if (product) {
            crumbs.push({ label: product.name });
        }

        return crumbs;
    }, [product, fromPath, location.state]);

    const showZeroStockWarning = useMemo(() => {
        if (
            !product ||
            pickupPointsLoading ||
            !hasPickupPoints ||
            product.sellerId == null ||
            product.pickupStocks === undefined
        ) {
            return false;
        }
        const byPointId = new Map(
            product.pickupStocks.map((s) => [s.pickupPointId, s]),
        );
        return pickupPoints.some((pp) => {
            const row = byPointId.get(pp.id);
            const qty = row?.quantity ?? 0;
            const unlimited = Boolean(row?.unlimitedStock);
            return !unlimited && qty === 0;
        });
    }, [product, pickupPointsLoading, hasPickupPoints, pickupPoints]);

    const handleBack = () => {
        // Если перешли с отзывов, возвращаемся к отзывам
        if (location.state?.from === 'reviews') {
            navigate('/reviews');
            return;
        }
        // Если перешли из заказа, возвращаемся к заказу
        if (fromOrder && orderId) {
            navigate(`/orders/${orderId}`);
            return;
        }
        if (fromPath === '/promotion') {
            navigate('/promotion', {
                state: location.state?.promotionTab
                    ? { promotionTab: location.state.promotionTab }
                    : undefined,
            });
            return;
        }

        // Находим предпоследний breadcrumb (последний - это текущая страница товара)
        if (breadcrumbs.length > 1) {
            const previousBreadcrumb = breadcrumbs[breadcrumbs.length - 2];
            if (previousBreadcrumb.path) {
                navigate(previousBreadcrumb.path);
            } else {
                // Если у предыдущего breadcrumb нет path, переходим на главную
                navigate('/');
            }
        } else {
            // Если breadcrumbs только один (Главная), переходим на главную
            navigate('/');
        }
    };



    const productStillLoading =
        !product && (productLoading || (Boolean(productError) && productFetching));

    if (productStillLoading) {
        return (
            <div className="page-container">
                <Breadcrumbs items={breadcrumbs} />
                <div className="empty-container">
                    <Loader />
                </div>
            </div>
        );
    }

    if (productError && !product) {
        return (
            <div className="page-container">
                <Breadcrumbs items={breadcrumbs} />
                <div className="empty-container">
                    <RefetchButton
                        refetch={() => refetchProduct()}
                        isLoading={productLoading}
                    />
                </div>
            </div>
        );
    }

    if (!product) {
        return (
            <div className="page-container">
                <Breadcrumbs items={breadcrumbs} />
                <div className="empty-container">
                    <div>Товар не найден</div>
                </div>
            </div>
        );
    }

    const cannotShowWithoutPickupPoints =
        product.isHidden && (pickupPointsLoading || !hasPickupPoints);

    const handleSetHidden = async () => {
        if (!productIdNum) return;

        if (product.isHidden && (!product.images || product.images.length === 0)) {
            setShowPhotosBeforeShowModal(true);
            return;
        }

        // Перед показом OOS-товара сначала нужно указать остатки
        if (
            product.isHidden &&
            product.isOutOfStock &&
            product.sellerId != null
        ) {
            setStocksModalBeforeShowHint(true);
            setShowManageStocksModal(true);
            return;
        }

        try {
            await setHiddenMutation.mutateAsync({
                id: productIdNum,
                value: !product?.isHidden
            });
            notifications.show({
                title: 'Успешно',
                message: product?.isHidden ? 'Товар успешно показан' : 'Товар успешно скрыт',
                color: 'green',
            });
        } catch (error) {
            const msg =
                error && typeof error === 'object' && 'response' in error
                    ? (error as { response?: { data?: { message?: string } } }).response?.data
                        ?.message
                    : undefined;
            notifications.show({
                title: 'Ошибка',
                message: msg ?? 'Ошибка при изменении видимости товара',
                color: 'red',
            });
        }
    };

    const handleEdit = () => {
        if (!productIdNum) return;
        if (isMobile) {
            navigate(`/products/${productIdNum}/edit`, {
                state: { from: `/products/${productIdNum}` },
            });
            return;
        }
        setShowEditProductModal(true);
    };

    const handleEditProductSuccess = (result?: { wentToModeration: boolean }) => {
        if (result?.wentToModeration) {
            setShowModerationInfoModal(true);
        }
    };

    const handleDelete = () => {
        setIsDeleteProductModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!productIdNum) return;
        try {
            // Получаем список ID из вариантов до удаления
            const variantIds = product?.variants && product.variants.length > 0
                ? product.variants.map(v => v.id)
                : [];

            // Получаем список ID подборок, в которых находится товар
            const customSetIds = product?.customSets && product.customSets.length > 0
                ? product.customSets.map(csp => csp.customSetId)
                : [];

            await deleteMutation.mutateAsync(productIdNum);

            // Инвалидируем кэш для всех продуктов из вариантов
            if (variantIds.length > 0) {
                variantIds.forEach(variantId => {
                    queryClient.invalidateQueries({ queryKey: ['product', variantId] });
                });
            }

            // Инвалидируем и перезапрашиваем конкретные подборки, в которых был товар
            if (customSetIds.length > 0) {
                customSetIds.forEach(customSetId => {
                    // Инвалидируем список продуктов подборки
                    queryClient.invalidateQueries({ queryKey: ['customSetProducts', customSetId] });
                    // Инвалидируем данные подборки
                    queryClient.invalidateQueries({ queryKey: ['customSet', customSetId] });
                });
            }

            notifications.show({
                title: 'Успешно',
                message: 'Товар успешно удален',
                color: 'green',
            });
            setIsDeleteProductModalOpen(false);

            // Возвращаемся на предыдущий breadcrumb
            if (breadcrumbs.length > 1) {
                const previousBreadcrumb = breadcrumbs[breadcrumbs.length - 2];
                if (previousBreadcrumb.path) {
                    navigate(previousBreadcrumb.path);
                } else {
                    // Если у предыдущего breadcrumb нет path, переходим на главную
                    navigate('/');
                }
            } else {
                // Если breadcrumbs только один (Главная), переходим на главную
                navigate('/');
            }
        } catch (error) {
            notifications.show({
                title: 'Ошибка',
                message: 'Ошибка при удалении товара',
                color: 'red',
            });
        }
    };

    const cancelDelete = () => {
        setIsDeleteProductModalOpen(false);
    };

    const handleRemoveFromCategory = (categoryId: number, categoryName: string, e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        setCategoryToRemove({ id: categoryId, name: categoryName });
        setIsDeleteCategoryModalOpen(true);
    };

    const confirmRemoveFromCategory = async () => {
        if (!categoryToRemove || !productIdNum) return;

        try {
            await removeFromCategory.mutateAsync({
                productId: productIdNum,
                categoryId: categoryToRemove.id,
            });

            notifications.show({
                title: 'Успешно',
                message: 'Товар успешно удален из категории',
                color: 'green',
            });

            setIsDeleteCategoryModalOpen(false);
            setCategoryToRemove(null);
        } catch (error: any) {
            console.error('Ошибка при удалении товара из категории:', error);
            notifications.show({
                title: 'Ошибка',
                message: error?.response?.data?.message || 'Ошибка при удалении товара из категории',
                color: 'red',
            });
        }
    };

    const cancelRemoveFromCategory = () => {
        setIsDeleteCategoryModalOpen(false);
        setCategoryToRemove(null);
    };

    const handleSavePromotionSettings = async () => {
        if (!productIdNum || product?.sellerId == null) return;
        if (cpcPromotionEnabled && cpcCreditExhausted) {
            notifications.show({
                title: 'Баланс пуст',
                message:
                    'Нельзя включить продвижение: рекламный баланс пуст. Пополните его в разделе «Продвижение».',
                color: 'red',
            });
            return;
        }
        if (cpcPromotionEnabled && cpcBudgetPaused) {
            notifications.show({
                title: 'Лимит бюджета',
                message:
                    'Недельный бюджет продвижения исчерпан. Увеличьте бюджет на странице «Продвижение», чтобы возобновить.',
                color: 'red',
            });
            return;
        }
        if (
            cpcPromotionEnabled &&
            advertisingCredit &&
            parsedPromotionCpc < advertisingCredit.cpcMinBidRub
        ) {
            notifications.show({
                title: 'Ставка слишком низкая',
                message: `Минимум ${formatRub(advertisingCredit.cpcMinBidRub)} ₽ за клик.`,
                color: 'red',
            });
            return;
        }
        if (
            cpcPromotionEnabled &&
            advertisingCredit &&
            parsedPromotionCpc > advertisingCredit.cpcMaxBidRub
        ) {
            notifications.show({
                title: 'Ставка слишком высокая',
                message: `Максимум ${formatRub(advertisingCredit.cpcMaxBidRub)} ₽ за клик.`,
                color: 'red',
            });
            return;
        }
        const cpcRub = parsedPromotionCpc;
        try {
            await updateProductMutation.mutateAsync({
                id: productIdNum,
                data: { cpcRub, promotionEnabled: cpcPromotionEnabled },
            });
            await queryClient.invalidateQueries({
                queryKey: ['seller-advertising-credit'],
            });
            notifications.show({
                title: 'Сохранено',
                message: 'Настройки продвижения обновлены',
                color: 'green',
            });
        } catch (error: unknown) {
            const message =
                (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
                'Не удалось сохранить настройки';
            notifications.show({
                title: 'Ошибка',
                message: String(message),
                color: 'red',
            });
        }
    };

    // Преобразуем значения в числа, если они пришли как строки
    const price = typeof product.price === 'string' ? parseFloat(product.price) : product.price;
    const hasDiscount = !!(product.discount && product.discountedPrice);
    const productImages = product.images ?? [];
    const heroImage =
        productImages.find((img) => img.id === previewImageId) ?? productImages[0] ?? null;
    const activePreviewId = heroImage?.id ?? null;

    const categoryChips =
        product.categories && product.categories.length > 0
            ? product.categories.map((item) => ({
                  id: item.category?.id ?? item.categoryId,
                  name: item.category?.name ?? `Категория #${item.categoryId}`,
                  sectionName:
                      item.category?.sections?.find((s) => s.section?.name)?.section?.name ?? null,
              }))
            : product.category
              ? [
                    {
                        id: product.category.id,
                        name: product.category.name,
                        sectionName:
                            product.category.sections?.find((s) => s.section?.name)?.section?.name ??
                            null,
                    },
                ]
              : [];

    const scrollThumbs = (direction: -1 | 1) => {
        thumbsTrackRef.current?.scrollBy({
            top: direction * 72,
            behavior: 'smooth',
        });
    };


    return (
        <div className={`page-container ${styles.productPage}`}>
            <Breadcrumbs items={breadcrumbs} />
            <div className="page-header">
                <div className={styles.pageHeaderTop}>
                    <div className={styles.productHeaderTitleContainer}>
                        <div className={styles.productHeaderBack}>
                            <BackButton onClick={handleBack} />
                            {fromOrder && (
                                <Button
                                    variant="secondary"
                                    onClick={handleBack}
                                    style={{ padding: '4px 8px', height: 'auto' }}
                                >
                                    Назад к заказу
                                </Button>
                            )}
                        </div>
                        <h1 title={product.name}>{product.name}</h1>
                    </div>
                    <div className={styles.pageHeaderActions}>
                        <button
                            type="button"
                            className={styles.deleteLink}
                            onClick={handleDelete}
                            disabled={deleteMutation.isPending}
                        >
                            {deleteMutation.isPending ? 'Удаление…' : 'Удалить'}
                        </button>
                    </div>
                </div>
            </div>

            <div className={styles.productMain}>
                <div className={styles.productDetails}>
                    {productImages.length > 0 && heroImage && (
                        <div className={styles.productGallery}>
                            {productImages.length > 1 && (
                                <div className={styles.productThumbsCol}>
                                    <button
                                        type="button"
                                        className={styles.productThumbNav}
                                        onClick={() => scrollThumbs(-1)}
                                        aria-label="Предыдущие превью"
                                    >
                                        <IoChevronUp size={18} aria-hidden />
                                    </button>
                                    <div
                                        ref={thumbsTrackRef}
                                        className={styles.productThumbs}
                                    >
                                        {productImages.map((image, index) => (
                                            <button
                                                key={image.id}
                                                type="button"
                                                className={`${styles.productImageItemWrapper} ${image.id === activePreviewId
                                                    ? styles.productImageItemWrapperSelected
                                                    : ''
                                                    }`}
                                                aria-pressed={image.id === activePreviewId}
                                                aria-label={`Превью ${index + 1}`}
                                                onClick={() => setPreviewImageId(image.id)}
                                            >
                                                <div
                                                    className={`${styles.productImageItem} ${image.id === activePreviewId
                                                        ? styles.productImageItemActive
                                                        : ''
                                                        }`}
                                                >
                                                    <CachedImage
                                                        src={productThumbSrc(image) || image.url}
                                                        alt={product.name}
                                                        loading="lazy"
                                                    />
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                    <button
                                        type="button"
                                        className={styles.productThumbNav}
                                        onClick={() => scrollThumbs(1)}
                                        aria-label="Следующие превью"
                                    >
                                        <IoChevronDown size={18} aria-hidden />
                                    </button>
                                </div>
                            )}
                            <div className={styles.productHero}>
                                <CachedImage
                                    src={heroImage.url}
                                    alt={product.name}
                                    loading="lazy"
                                />
                            </div>
                        </div>
                    )}

                    <div className={styles.productAside}>
                        {((product.approved === null || product.approved === undefined) ||
                            product.approved === false ||
                            showZeroStockWarning) && (
                            <div className={styles.productAlerts}>
                                {(product.approved === null || product.approved === undefined) && (
                                    <div className={`${styles.moderationBanner} ${styles.moderationBannerPending}`}>
                                        <p className={styles.moderationBannerTitle}>На модерации</p>
                                        <p className={styles.moderationBannerText}>
                                            Товар отправлен на проверку. После одобрения он появится в каталоге
                                            для покупателей.
                                        </p>
                                    </div>
                                )}
                                {product.approved === false && (
                                    <div className={`${styles.moderationBanner} ${styles.moderationBannerRejected}`}>
                                        <p className={styles.moderationBannerTitle}>Товар отклонён</p>
                                        <p className={styles.moderationBannerText}>
                                            <strong>Причина:</strong>{' '}
                                            {product.moderationRejectionReason?.trim() ||
                                                'Администратор не указал причину.'}
                                        </p>
                                        <p className={styles.moderationBannerText}>
                                            Внесите правки и сохраните товар — он снова уйдёт на модерацию.
                                        </p>
                                    </div>
                                )}
                                {showZeroStockWarning && (
                                    <div
                                        className={`${styles.moderationBanner} ${styles.moderationBannerStock}`}
                                        role="status"
                                    >
                                        <p className={styles.moderationBannerTitle}>Проверьте остатки</p>
                                        <p className={styles.moderationBannerText}>
                                            На части точек нулевой остаток. Укажите количество, чтобы товар
                                            был доступен к заказу.
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}

                    <div className={styles.productInfo}>
                        <div className={styles.productActions} role="toolbar" aria-label="Действия с товаром">
                            {product.sellerId != null && (
                                <span className={styles.productActionWithBadge}>
                                    <button
                                        type="button"
                                        className={styles.productActionChip}
                                        onClick={() => setShowManageStocksModal(true)}
                                        title={
                                            showZeroStockWarning
                                                ? 'Есть точки с нулевым остатком'
                                                : undefined
                                        }
                                    >
                                        <IoCubeOutline size={16} aria-hidden />
                                        Остатки
                                    </button>
                                    {showZeroStockWarning && (
                                        <span
                                            className={styles.productActionBadgeDot}
                                            aria-hidden
                                        />
                                    )}
                                </span>
                            )}
                            <button
                                type="button"
                                className={styles.productActionChip}
                                onClick={() => setShowDiscountModal(true)}
                            >
                                <IoPricetagOutline size={16} aria-hidden />
                                {hasDiscount ? 'Изм. скидку' : 'Скидка'}
                            </button>
                            <button
                                type="button"
                                className={styles.productActionChip}
                                onClick={handleSetHidden}
                                disabled={
                                    setHiddenMutation.isPending ||
                                    cannotShowWithoutPickupPoints
                                }
                                title={
                                    cannotShowWithoutPickupPoints
                                        ? 'Сначала добавьте точку самовывоза'
                                        : undefined
                                }
                            >
                                {setHiddenMutation.isPending ? (
                                    <Loader size={14} type="oval" color="currentColor" />
                                ) : product.isHidden ? (
                                    <IoEyeOutline size={16} aria-hidden />
                                ) : (
                                    <IoEyeOffOutline size={16} aria-hidden />
                                )}
                                {product.isHidden ? 'Показать' : 'Скрыть'}
                            </button>
                            <button
                                type="button"
                                className={styles.productActionChip}
                                onClick={handleEdit}
                            >
                                <IoCreateOutline size={16} aria-hidden />
                                Редактировать
                            </button>
                            {/* {product.sellerId != null && (
                                <button
                                    type="button"
                                    className={`${styles.productActionChip} ${styles.productActionChipAccent}`}
                                    onClick={() => setShowCpcPromotionModal(true)}
                                    aria-label="Открыть настройки продвижения в каталоге (CPC)"
                                >
                                    <IoArrowUp size={16} aria-hidden />
                                    Продвижение
                                </button>
                            )} */}
                            {product.sellerId != null && (
                                <button
                                    type="button"
                                    className={`${styles.productActionChip} ${styles.productActionChipAccent}`}
                                    onClick={() => navigate('/promotion')}
                                    aria-label="Открыть страницу продвижения"
                                >
                                    <IoArrowUp size={16} aria-hidden />
                                    Продвижение
                                </button>
                            )}
                        </div>

                        <section className={styles.card}>
                            <div className={styles.priceBlock}>
                                <div className={styles.summaryTitleRow}>
                                    <h2 className={styles.summaryName}>{product.name}</h2>
                                    <div className={styles.summaryCategoryChips}>
                                        {categoryChips.length === 0 ? (
                                            <span className={styles.summaryCategoryChipMuted}>
                                                Без категории
                                            </span>
                                        ) : (
                                            categoryChips.map((chip) => (
                                                <span key={chip.id} className={styles.summaryCategoryChip}>
                                                    {chip.sectionName && (
                                                        <>
                                                            <span className={styles.summaryCategoryChipSection}>
                                                                {chip.sectionName}
                                                            </span>
                                                            <span aria-hidden>•</span>
                                                        </>
                                                    )}
                                                    {chip.name}
                                                    {categoryChips.length > 1 && (
                                                        <button
                                                            type="button"
                                                            className={styles.summaryCategoryChipRemove}
                                                            title="Удалить из категории"
                                                            disabled={removeFromCategory.isPending}
                                                            onClick={(e) =>
                                                                handleRemoveFromCategory(chip.id, chip.name, e)
                                                            }
                                                        >
                                                            ×
                                                        </button>
                                                    )}
                                                </span>
                                            ))
                                        )}
                                    </div>
                                </div>
                                <div className={styles.summaryStats}>
                                    <span className={styles.summaryStat} title="ID">
                                        #{product.id}
                                    </span>
                                    {product.rating != null ? (
                                        <span className={styles.summaryStat} title="Рейтинг">
                                            ★ {Number(product.rating)}
                                        </span>
                                    ) : (
                                        <span className={styles.summaryStat} title="Рейтинг">
                                            ★ —
                                        </span>
                                    )}
                                    <span className={styles.summaryStat} title="Куплено">
                                        <IoCartOutline size={14} aria-hidden />
                                        {product.purchaseCount ?? 0}
                                    </span>
                                    <span className={styles.summaryStat} title="Просмотрено">
                                        <IoEyeOutline size={14} aria-hidden />
                                        {product.viewedCount ?? 0}
                                    </span>
                                </div>
                                <ProductPrice
                                    price={price}
                                    discount={product.discount}
                                    discountedPrice={product.discountedPrice}
                                />
                                <ProductBadges
                                    productId={product.id}
                                    hasDiscount={hasDiscount}
                                    isPopular={product.isPopular}
                                    isNew={product.isNew}
                                    isHidden={product.isHidden}
                                    isOutOfStock={product.isOutOfStock}
                                    promotionEnabled={Boolean(product.promotionEnabled)}
                                    customSets={product.customSets?.map((csp) => ({
                                        id: csp.customSetId,
                                        name:
                                            csp.customSet?.name ||
                                            `Подборка #${csp.customSetId}`,
                                    }))}
                                    direction="row"
                                />
                            </div>
                        </section>

                        <section className={styles.card}>
                            <div className={styles.infoTabs} role="tablist" aria-label="Информация о товаре">
                                {(
                                    [
                                        ['description', 'Описание'],
                                        ['composition', 'Состав'],
                                        ['specs', isMobile ? 'Хар-ки' : 'Характеристики'],
                                    ] as const
                                ).map(([id, label]) => (
                                    <button
                                        key={id}
                                        type="button"
                                        role="tab"
                                        aria-selected={infoTab === id}
                                        className={`${styles.infoTab} ${infoTab === id ? styles.infoTabActive : ''
                                            }`}
                                        onClick={() => setInfoTab(id)}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>

                            <div className={styles.cardBody} role="tabpanel">
                                {infoTab === 'description' && (
                                    <p className={styles.descriptionText}>
                                        {product.description?.trim() ? (
                                            product.description
                                        ) : (
                                            <span className={styles.rowValueMuted}>Нет описания</span>
                                        )}
                                    </p>
                                )}

                                {infoTab === 'composition' && (
                                    <>
                                        <ProductField label="Сборка">
                                            {product.assemblyTimeMinutes != null &&
                                                product.assemblyTimeMinutes > 0 ? (
                                                `${product.assemblyTimeMinutes} мин`
                                            ) : (
                                                <span className={styles.rowValueMuted}>—</span>
                                            )}
                                        </ProductField>
                                        <ProductField label="Состав">
                                            {(() => {
                                                if (
                                                    !product.composition ||
                                                    !Array.isArray(product.composition) ||
                                                    product.composition.length === 0
                                                ) {
                                                    return (
                                                        <span className={styles.rowValueMuted}>—</span>
                                                    );
                                                }
                                                const items = product.composition.filter(
                                                    (item: { value?: string; count?: number }) =>
                                                        item?.value?.trim(),
                                                );
                                                if (items.length === 0) {
                                                    return (
                                                        <span className={styles.rowValueMuted}>—</span>
                                                    );
                                                }
                                                return (
                                                    <ul className={styles.compositionList}>
                                                        {items.map(
                                                            (
                                                                item: {
                                                                    value: string;
                                                                    count: number;
                                                                },
                                                                i: number,
                                                            ) => (
                                                                <li
                                                                    key={i}
                                                                    className={styles.compositionItem}
                                                                >
                                                                    <span className={styles.compositionValue}>
                                                                        {item.value}
                                                                    </span>
                                                                    <span
                                                                        className={
                                                                            styles.compositionCount
                                                                        }
                                                                    >
                                                                        {item.count != null &&
                                                                            item.count > 0
                                                                            ? item.count
                                                                            : '—'}
                                                                    </span>
                                                                </li>
                                                            ),
                                                        )}
                                                    </ul>
                                                );
                                            })()}
                                        </ProductField>
                                    </>
                                )}

                                {infoTab === 'specs' && (
                                    <>
                                        <ProductField label="Высота">
                                            {product.height ? (
                                                `${product.height} см`
                                            ) : (
                                                <span className={styles.rowValueMuted}>—</span>
                                            )}
                                        </ProductField>
                                        <ProductField label="Ширина">
                                            {product.width ? (
                                                `${product.width} см`
                                            ) : (
                                                <span className={styles.rowValueMuted}>—</span>
                                            )}
                                        </ProductField>
                                        <ProductField label="Вес">
                                            {product.weight != null ? (
                                                `${product.weight} г`
                                            ) : (
                                                <span className={styles.rowValueMuted}>—</span>
                                            )}
                                        </ProductField>
                                        <ProductField label="Типы цветков">
                                            {product.flowerTypes &&
                                                product.flowerTypes.length > 0
                                                ? product.flowerTypes
                                                    .map((pft) => pft.flowerType?.name)
                                                    .filter(Boolean)
                                                    .join(', ')
                                                : product.flowerType?.name || (
                                                    <span className={styles.rowValueMuted}>
                                                        —
                                                    </span>
                                                )}
                                        </ProductField>
                                        <ProductField label="Кол-во цветов">
                                            {product.flowerCount || (
                                                <span className={styles.rowValueMuted}>—</span>
                                            )}
                                        </ProductField>
                                        <ProductField label="Цвета">
                                            {product.colors && product.colors.length > 0
                                                ? product.colors
                                                    .map((pc) => pc.color?.name || '')
                                                    .filter(Boolean)
                                                    .join(', ') || (
                                                    <span className={styles.rowValueMuted}>
                                                        —
                                                    </span>
                                                )
                                                : (
                                                    <span className={styles.rowValueMuted}>
                                                        —
                                                    </span>
                                                )}
                                        </ProductField>
                                        <ProductField label="Тип сборки">
                                            {product.assemblyType?.name || (
                                                <span className={styles.rowValueMuted}>—</span>
                                            )}
                                        </ProductField>
                                    </>
                                )}
                            </div>
                        </section>

                    </div>
                    </div>

                </div>

                <div className={styles.productVariantsSection}>
                    <div className={styles.productVariantsHeader}>
                        <h3>Варианты товара</h3>
                        <Button
                            onClick={() => setShowManageVariantsModal(true)}
                            variant="secondary"
                        >
                            Настроить
                        </Button>
                    </div>
                    {product.variants && product.variants.length > 0 ? (
                        <div className={styles.productVariantsList}>
                            {product.variants.map((variant) => {
                                const variantFirstImage =
                                    variant.images && variant.images.length > 0
                                        ? variant.images[0]
                                        : null;
                                const isCurrentVariant = variant.id === productIdNum;
                                const nextFromPath =
                                    fromPath && !fromPath.match(/^\/products\/(\d+)$/)
                                        ? fromPath
                                        : undefined;
                                return (
                                    <div
                                        key={variant.id}
                                        className={`${styles.productVariantItem} ${isCurrentVariant
                                            ? styles.productVariantItemActive
                                            : ''
                                            }`}
                                        onClick={
                                            isCurrentVariant
                                                ? undefined
                                                : () =>
                                                    navigate(`/products/${variant.id}`, {
                                                        state: { from: nextFromPath },
                                                    })
                                        }
                                        style={{
                                            cursor: isCurrentVariant ? 'default' : 'pointer',
                                        }}
                                    >
                                        {variantFirstImage && (
                                            <div className={styles.productVariantImage}>
                                                <CachedImage
                                                    src={productThumbSrc(variantFirstImage) || variantFirstImage.url}
                                                    alt={variant.name}
                                                    loading="lazy"
                                                />
                                            </div>
                                        )}
                                        <div className={styles.productVariantInfo}>
                                            {variant.variantName &&
                                                variant.variantName.trim() !==
                                                variant.name.trim() && (
                                                    <div className={styles.productVariantName}>
                                                        {variant.variantName}
                                                    </div>
                                                )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <p className={styles.productVariantsEmpty}>
                            Вариантов пока нет. Нажмите «Настроить», чтобы добавить.
                        </p>
                    )}
                </div>

                <div className={styles.productVariantsSection}>
                    <div className={styles.productVariantsHeader}>
                        <h3>Дополнительные предложения</h3>
                        <Button
                            onClick={() => setShowManageExtrasModal(true)}
                            variant="secondary"
                        >
                            Настроить
                        </Button>
                    </div>
                    {productExtras.length > 0 ? (
                        <div className={styles.productVariantsList}>
                            {productExtras.map((extra) => {
                                const extraImage =
                                    extra.images && extra.images.length > 0
                                        ? extra.images[0]
                                        : null;
                                return (
                                    <div
                                        key={extra.id}
                                        className={styles.productVariantItem}
                                        onClick={() =>
                                            navigate(`/products/${extra.id}`, {
                                                state: {
                                                    from:
                                                        fromPath &&
                                                            !fromPath.match(/^\/products\/(\d+)$/)
                                                            ? fromPath
                                                            : `/products/${productIdNum}`,
                                                },
                                            })
                                        }
                                        style={{ cursor: 'pointer' }}
                                    >
                                        {extraImage && (
                                            <div className={styles.productVariantImage}>
                                                <CachedImage
                                                    src={productThumbSrc(extraImage) || extraImage.url}
                                                    alt={extra.name}
                                                    loading="lazy"
                                                />
                                            </div>
                                        )}
                                        <div className={styles.productVariantInfo}>
                                            <div className={styles.productVariantName}>
                                                {extra.name}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <p className={styles.productVariantsEmpty}>
                            Допов пока нет. Нажмите «Настроить», чтобы добавить.
                        </p>
                    )}
                </div>

            </div>

            {product && (
                <SetDiscountModal
                    isOpen={showDiscountModal}
                    onClose={() => setShowDiscountModal(false)}
                    productId={productIdNum!}
                    product={product}
                />
            )}

            <AddToSetModal
                isOpen={isAddToSetModalOpen}
                onClose={() => setIsAddToSetModalOpen(false)}
                productIds={productIdNum ? [productIdNum] : []}
            />

            <ManageVariantsModal
                isOpen={showManageVariantsModal}
                onClose={() => setShowManageVariantsModal(false)}
                productId={productIdNum!}
            />

            {productIdNum != null && (
                <ManageProductExtrasModal
                    isOpen={showManageExtrasModal}
                    onClose={() => setShowManageExtrasModal(false)}
                    productId={productIdNum}
                    onSuccess={() => {
                        void refetchExtras();
                    }}
                />
            )}

            <AddToCategoryModal
                isOpen={showAddToCategoryModal}
                onClose={() => setShowAddToCategoryModal(false)}
                productId={productIdNum}
                currentCategoryIds={
                    product?.categories && product.categories.length > 0
                        ? product.categories.map(c => c.categoryId)
                        : product?.categoryId
                            ? [product.categoryId]
                            : []
                }
            />

            <DoubleCheckModal
                isOpen={isDeleteCategoryModalOpen}
                onClose={cancelRemoveFromCategory}
                title="Удалить из категории"
                message={
                    categoryToRemove
                        ? `Вы уверены, что хотите удалить товар из категории "${categoryToRemove.name}"?`
                        : 'Вы уверены, что хотите удалить товар из категории?'
                }
                secondButtonText="Удалить"
                secondButtonAction={confirmRemoveFromCategory}
                isLoading={removeFromCategory.isPending}
            />

            <DoubleCheckModal
                isOpen={isDeleteProductModalOpen}
                onClose={cancelDelete}
                title="Удалить товар"
                message="Вы уверены, что хотите удалить этот товар? Это действие нельзя отменить."
                secondButtonText="Удалить"
                secondButtonAction={confirmDelete}
                isLoading={deleteMutation.isPending}
            />

            {productIdNum && (
                <EditProductModal
                    isOpen={showEditProductModal}
                    onClose={() => setShowEditProductModal(false)}
                    productId={productIdNum}
                    onSuccess={handleEditProductSuccess}
                />
            )}

            {product && (
                <PhotosBeforeShowModal
                    isOpen={showPhotosBeforeShowModal}
                    onClose={() => setShowPhotosBeforeShowModal(false)}
                    onUpload={handleEdit}
                />
            )}

            {product && product.sellerId != null && (
                <ManageProductStocksModal
                    isOpen={showManageStocksModal}
                    onClose={handleCloseManageStocksModal}
                    product={product}
                    onSaved={
                        stocksModalBeforeShowHint
                            ? handleStocksSavedBeforeShow
                            : () => {
                                void refetchProduct();
                            }
                    }
                    afterCreateHint={stocksModalAfterCreateHint}
                    beforeShowHint={stocksModalBeforeShowHint}
                />
            )}

            <Modal
                isOpen={showModerationInfoModal}
                onClose={() => setShowModerationInfoModal(false)}
                title="Товар отправлен на модерацию"
                maxWidth={440}
                classNames={{ body: styles.moderationInfoModalBody }}
            >
                <div className={styles.moderationInfoModal}>
                    <p className={styles.moderationInfoModalText}>
                        Товар появится в каталоге после проверки модератором.
                    </p>
                    <div className={styles.moderationInfoModalActions}>
                        <Button onClick={() => setShowModerationInfoModal(false)}>
                            Понятно
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Модалка продвижения отключена: кнопка ведёт на страницу продвижения. */}
            {false && product?.sellerId != null && (
                <Modal
                    isOpen={showCpcPromotionModal}
                    onClose={() => setShowCpcPromotionModal(false)}
                    title={
                        <div className={styles.cpcModalTitle}>
                            <span>Продвижение в каталоге</span>
                            <button
                                type="button"
                                className={styles.cpcModalInfoBtn}
                                aria-label="Как работает платное продвижение в каталоге"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowCpcInfoModal(true);
                                }}
                            >
                                <IoInformationCircleOutline size={18} aria-hidden />
                            </button>
                        </div>
                    }
                    maxWidth={480}
                >
                    <div className={styles.cpcModal}>
                        <p className={styles.cpcModalHint}>
                            Ставка учитывается в выдаче города покупателя после пересчёта — обычно
                            в течение 10 минут. Менять ставку или включать/выключать продвижение
                            можно не чаще одного раза в 15 минут.
                        </p>

                        {advertisingCreditLoading ? (
                            <div className={styles.cpcModalLoader}>
                                <Loader size="sm" />
                            </div>
                        ) : null}

                        {cpcCreditExhausted && advertisingCredit ? (
                            <div
                                className={`${styles.cpcNotice} ${styles.cpcNoticeError}`}
                                role="status"
                            >
                                <p className={styles.cpcNoticeTitle}>Рекламный баланс пуст</p>
                                <p className={styles.cpcNoticeText}>
                                    Клики не списываются, пока баланс не пополнен. Товары и ставки сохраняются — выставите счёт в разделе «Продвижение».
                                </p>
                            </div>
                        ) : null}

                        {cpcBudgetPaused && !cpcCreditExhausted ? (
                            <div
                                className={`${styles.cpcNotice} ${styles.cpcNoticeError}`}
                                role="status"
                            >
                                <p className={styles.cpcNoticeTitle}>Лимит бюджета достигнут</p>
                                <p className={styles.cpcNoticeText}>
                                    Недельный бюджет исчерпан. Товары и ставки сохранены — увеличьте бюджет на странице «Продвижение» или дождитесь новой недели.
                                </p>
                            </div>
                        ) : null}

                        {!cpcAdsBlocked && cpcBelowMinBid && advertisingCredit ? (
                            <div
                                className={`${styles.cpcNotice} ${styles.cpcNoticeWarning}`}
                                role="status"
                            >
                                <p className={styles.cpcNoticeTitle}>Слишком низкая ставка</p>
                                <p className={styles.cpcNoticeText}>
                                    Минимум за клик — {cpcMinBidLabel} ₽.
                                    Повысьте ставку.
                                </p>
                            </div>
                        ) : null}

                        {!cpcAdsBlocked && cpcAboveMaxBid && advertisingCredit ? (
                            <div
                                className={`${styles.cpcNotice} ${styles.cpcNoticeWarning}`}
                                role="status"
                            >
                                <p className={styles.cpcNoticeTitle}>Слишком высокая ставка</p>
                                <p className={styles.cpcNoticeText}>
                                    Максимум за клик — {cpcMaxBidLabel} ₽.
                                    Снизьте ставку.
                                </p>
                            </div>
                        ) : null}

                        {promotionSettingsOnCooldown ? (
                            <div
                                className={`${styles.cpcNotice} ${styles.cpcNoticeMuted}`}
                                role="status"
                            >
                                <p className={styles.cpcNoticeTitle}>Подождите</p>
                                <p className={styles.cpcNoticeText}>
                                    Настройки продвижения недавно меняли. Следующее изменение через ~
                                    {promotionCooldownMinutesLeft} мин.
                                </p>
                            </div>
                        ) : null}

                        {/* {rankPending ? (
                            <div
                                className={`${styles.cpcNotice} ${styles.cpcNoticeInfo}`}
                                role="status"
                            >
                                <p className={styles.cpcNoticeTitle}>Идёт пересчёт</p>
                                <p className={styles.cpcNoticeText}>
                                    Позиция в выдаче города и списание за клики применятся после
                                    пересчёта ранга (обычно до 10 минут).
                                </p>
                            </div>
                        ) : null} */}
                        {/* 
                            {!cpcCreditExhausted && advertisingCredit ? (
                                <p className={styles.cpcModalMeta}>
                                    Доступно к рекламе:{' '}
                                    <strong>
                                        {formatRub(advertisingCredit.availableForAdsRub)} ₽
                                    </strong>
                                    {' '}
                                    (к выплате {formatRub(advertisingCredit.payableBalanceRub)} ₽,
                                    овердрафт до −{formatRub(advertisingCredit.creditLimitRub)} ₽)
                                </p>
                            ) : null} */}

                        <div className={styles.cpcModalCard}>
                            <div className={styles.cpcSwitchRow}>
                                <div className={styles.cpcSwitchCopy}>
                                    <p className={styles.cpcSwitchLabel}>Платное продвижение</p>
                                    {(cpcEnableLocked ||
                                        cpcAdsBlocked ||
                                        promotionSettingsOnCooldown) && (
                                        <p className={styles.cpcSwitchDesc}>
                                            {cpcAdsBlocked
                                                ? cpcCreditExhausted
                                                    ? 'Недоступно: рекламный баланс пуст'
                                                    : 'Недоступно: достигнут недельный бюджет'
                                                : cpcEnableLocked
                                                    ? cpcBelowMinBid
                                                        ? `Недоступно: мин. ${advertisingCredit?.cpcMinBidRub ?? 10} ₽ за клик`
                                                        : `Недоступно: макс. ${advertisingCredit?.cpcMaxBidRub ?? 500} ₽ за клик`
                                                    : `Изменение через ~${promotionCooldownMinutesLeft} мин`}
                                        </p>
                                    )}
                                </div>
                                <Switch
                                    checked={cpcPromotionEnabled}
                                    disabled={
                                        cpcEnableLocked ||
                                        cpcAdsBlocked ||
                                        promotionSettingsOnCooldown
                                    }
                                    onChange={(event) =>
                                        setCpcPromotionEnabled(event.currentTarget.checked)
                                    }
                                    aria-label="Платное продвижение"
                                />
                            </div>

                            <div className={styles.productPromotionRow}>
                                <Input
                                    label="Цена за клик, ₽"
                                    placeholder="0"
                                    type="number"
                                    min={advertisingCredit?.cpcMinBidRub ?? 0}
                                    max={advertisingCredit?.cpcMaxBidRub ?? undefined}
                                    step={0.01}
                                    className={styles.cpcBidInput}
                                    value={cpcPromotionRub}
                                    disabled={
                                        promotionSettingsOnCooldown || cpcAdsBlocked
                                    }
                                    description={cpcBidRangeDescription}
                                    onChange={(e) =>
                                        setCpcPromotionRub(e.currentTarget.value)
                                    }
                                />
                                <Button
                                    type="button"
                                    onClick={handleSavePromotionSettings}
                                    loading={updateProductMutation.isPending}
                                    disabled={
                                        (cpcPromotionEnabled &&
                                            (cpcAdsBlocked ||
                                                cpcAboveMaxBid ||
                                                cpcBelowMinBid)) ||
                                        promotionSettingsOnCooldown
                                    }
                                >
                                    Сохранить
                                </Button>
                            </div>
                        </div>
                    </div>
                </Modal>
            )}

            {false && (
            <Modal
                isOpen={showCpcInfoModal}
                onClose={() => setShowCpcInfoModal(false)}
                title="Как работает продвижение"
                maxWidth={560}
            >
                <div className={styles.cpcModal}>
                    <p className={styles.cpcModalHint}>
                        Вы назначаете ставку за клик по карточке товара — в рублях. Чем она выше,
                        тем выше шансы попасть в верхнюю часть выдачи в городе покупателя
                        (категория, главная, поиск).
                    </p>

                    <div className={styles.cpcModalCard}>
                        <p className={styles.cpcInfoStepsTitle}>Чтобы начать</p>
                        <ol className={styles.cpcInfoSteps}>
                            <li>Включите тумблер «Платное продвижение».</li>
                            <li>Установите цену за клик.</li>
                            <li>
                                После пересчёта ранга (обычно до 10 минут) ставка попадёт в выдачу
                                вашего города, и платформа начнёт списывать деньги за клики.
                            </li>
                        </ol>
                    </div>

                    <div className={`${styles.cpcNotice} ${styles.cpcNoticeMuted}`} role="note">
                        <p className={styles.cpcNoticeTitle}>Важно</p>
                        <p className={styles.cpcNoticeText}>
                            Ставку и тумблер можно менять не чаще раза в 15 минут. Клики
                            списываются с рекламного баланса. Если на ставку товара не хватает
                            денег, продвижение этого товара останавливается. Вывести рекламный
                            баланс можно только на баланс магазина.
                        </p>
                    </div>

                    <div className={styles.cpcModalFooter}>
                        <a
                            href={sellerHelpUrl('cpc-what')}
                            className={styles.cpcModalHelpLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setShowCpcInfoModal(false)}
                        >
                            Подробнее в разделе «Помощь»
                        </a>
                        <Button onClick={() => setShowCpcInfoModal(false)}>Понятно</Button>
                    </div>
                </div>
            </Modal>
            )}
        </div>
    );
}